import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import CircleIconButton from "@components/buttons/CircleIconButton";
import BaseErrors from "@components/fields/BaseErrors";
import Input from "@components/fields/Input";
import Form from "@components/forms/Form";
import styles from "@components/forms/InviteAccountMemberForm.module.css";
import useCareTeamInviteCreateMutation from "@hooks/useCareTeamInviteCreateMutation";
import { JourneyFromSomeQuery } from "@typing/Models";
import { handleMutationResponse } from "@utils/formUtils";

type FormData = {
  email: string | null;
};

type Props = {
  consultScheduling?: boolean;
  journey: JourneyFromSomeQuery;
  onCancel: () => void;
  onSuccess?: (inviteEmail: string) => void;
};

const InviteAccountMemberForm = ({ consultScheduling, journey, onCancel, onSuccess }: Props) => {
  const careTeamInviteCreate = useCareTeamInviteCreateMutation(journey);
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      email: yup
        .string()
        .required(intl.formatMessage({ id: "errors.inviteAccountMembers.email.blank" }))
        .email(intl.formatMessage({ id: "errors.inviteAccountMembers.emailFormat" }))
    })
    .required();

  const defaultValues: FormData = {
    email: null
  };

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    const email = form.getValues().email;

    if (consultScheduling) return onSuccess?.(email ?? "");

    if (email)
      return careTeamInviteCreate({
        variables: {
          email,
          journeyId: journey.id
        }
      }).then(response => {
        if (response.data?.careTeamInviteCreate) {
          handleMutationResponse<FormData>({
            data: response.data.careTeamInviteCreate,
            intl,
            onSuccess: () => {
              form.reset();
              onSuccess?.(email);
            },
            setBaseErrors,
            setError: form.setError,
            txBase: "errors.inviteAccountMembers"
          });
        }
      });
  }, [careTeamInviteCreate, consultScheduling, form, intl, journey.id, onSuccess]);

  return (
    <Form data-test="invite-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />

      <div className={styles.relativeContainer}>
        <Input<FormData>
          control={form.control}
          data-test="email-input"
          error={form.formState.errors.email}
          extraRightSpacing
          hoverOutline
          name="email"
          noSpacing
          placeholder="email@withgrayce.com"
          type="email"
        />
        <div className={styles.submitIcons}>
          <CircleIconButton
            ariaLabelKey="accessibility.ariaLabels.add"
            color="success-500"
            disabled={!form?.formState.isValid || form?.formState.isSubmitting}
            icon="checkmark"
            type="submit"
          />
          <CircleIconButton
            ariaLabelKey="accessibility.ariaLabels.delete"
            color="danger-300"
            icon="xmark"
            onClick={() => {
              onCancel();
            }}
          />
        </div>
      </div>
    </Form>
  );
};

export default InviteAccountMemberForm;
